/**
 * Panel i18n. English is the source of truth (the report PDF is already Hebrew
 * via @enersu/report-generator); this adds an in-panel Hebrew/RTL view.
 *
 * Hebrew terminology is aligned with the report-generator messages
 * (`packages/report-generator/i18n/he.json`) for consistency. Coverage is the
 * panel's static chrome (header, form, analyses picker, buttons, status, notes);
 * dynamic pipeline outputs and the advanced tools stay English for now.
 */
export type PanelLang = 'en' | 'he';

export interface PanelStrings {
  dir: 'ltr' | 'rtl';
  subtitle: string;
  disclaimer: string;
  studyPackage: string;
  studyPackageCopy: string;
  projectName: string;
  municipality: string;
  buildingType: string;
  buildingTypes: Record<'unknown' | 'residential' | 'office' | 'mixed-use' | 'public', string>;
  pickAnalyses: string;
  pickAnalysesCopy: string;
  sun: string;
  noise: string;
  wind: string;
  sunMonth: string;
  sunDay: string;
  windScale: string;
  statusLabel: string;
  statusText: Record<'idle' | 'running' | 'complete' | 'error', string>;
  runPipeline: string;
  generatePdf: string;
  downloadPdf: string;
  detectedPrefix: string;
  municipalityHint: string;
  outsideIsrael: (country: string) => string;
  analysisStatus: Record<'complete' | 'needs-review' | 'error' | 'not-run', string>;
  progressLabel: string;
}

export const PANEL_STRINGS: Record<PanelLang, PanelStrings> = {
  en: {
    dir: 'ltr',
    subtitle: 'Israel Municipal Compliance Pipeline',
    disclaimer:
      'Technical screening & pre-check only — not a municipal approval or certified compliance report. Final certification requires a licensed Israeli sustainability consultant.',
    studyPackage: 'Municipal study package',
    studyPackageCopy: 'Run the local pilot pipeline against the current Forma selection.',
    projectName: 'Project name',
    municipality: 'Municipality',
    buildingType: 'Building type',
    buildingTypes: {
      unknown: 'Unknown',
      residential: 'Residential',
      office: 'Office',
      'mixed-use': 'Mixed use',
      public: 'Public',
    },
    pickAnalyses: 'Live screening — pick analyses',
    pickAnalysesCopy: 'Slower: triggers Forma native analyses on the current selection.',
    sun: '☀️ Sun & shadow — sun-hours screening',
    noise: '🔊 Noise — rail & road screening',
    wind: '💨 Wind — pedestrian comfort screening',
    sunMonth: 'Sun month',
    sunDay: 'Sun day',
    windScale: 'Wind comfort scale',
    statusLabel: 'Status',
    statusText: {
      idle: 'Ready for local compliance checks',
      running: 'Running local compliance pipeline',
      complete: 'Compliance pipeline complete',
      error: 'Compliance pipeline failed',
    },
    runPipeline: 'Run Compliance Pipeline',
    generatePdf: 'Generate PDF',
    downloadPdf: 'Download PDF',
    detectedPrefix: '📍 Detected from project location:',
    municipalityHint:
      'Validated pilot: Tel Aviv · other municipalities run as screening preview only.',
    outsideIsrael: (country) =>
      `⚠️ Project country is ${country} — EnerSU screening targets Israeli municipalities (pilot: Tel Aviv).`,
    analysisStatus: {
      complete: 'Complete',
      'needs-review': 'Needs review',
      error: 'Needs review',
      'not-run': 'Not run',
    },
    progressLabel: 'Compliance pipeline progress',
  },
  he: {
    dir: 'rtl',
    subtitle: 'תהליך בדיקת עמידה — רשויות מקומיות בישראל',
    disclaimer:
      'בדיקה טכנית מקדימה בלבד — אינה אישור עירוני או דוח עמידה מאושר. אישור סופי מחייב יועץ קיימות ישראלי מוסמך.',
    studyPackage: 'חבילת בדיקות עירונית',
    studyPackageCopy: 'הרצת תהליך הפיילוט המקומי על הבחירה הנוכחית ב‑Forma.',
    projectName: 'שם הפרויקט',
    municipality: 'רשות מקומית',
    buildingType: 'סוג מבנה',
    buildingTypes: {
      unknown: 'לא ידוע',
      residential: 'מגורים',
      office: 'משרדים',
      'mixed-use': 'שימוש מעורב',
      public: 'ציבורי',
    },
    pickAnalyses: 'בדיקה חיה — בחירת ניתוחים',
    pickAnalysesCopy: 'איטי יותר: מפעיל ניתוחים מובנים של Forma על הבחירה הנוכחית.',
    sun: '☀️ שמש וצללים — בדיקת שעות שמש',
    noise: '🔊 רעש — מסילה וכביש',
    wind: '💨 רוח — נוחות הולכי רגל',
    sunMonth: 'חודש (שמש)',
    sunDay: 'יום (שמש)',
    windScale: 'סולם נוחות רוח',
    statusLabel: 'סטטוס',
    statusText: {
      idle: 'מוכן לבדיקות עמידה מקומיות',
      running: 'מריץ תהליך בדיקת עמידה',
      complete: 'תהליך הבדיקה הושלם',
      error: 'תהליך הבדיקה נכשל',
    },
    runPipeline: 'הרץ בדיקת עמידה',
    generatePdf: 'צור PDF',
    downloadPdf: 'הורד PDF',
    detectedPrefix: '📍 זוהה לפי מיקום הפרויקט:',
    municipalityHint: 'פיילוט מאומת: תל אביב · רשויות אחרות מורצות כתצוגה מקדימה בלבד.',
    outsideIsrael: (country) =>
      `⚠️ מדינת הפרויקט היא ${country} — בדיקות EnerSU מיועדות לרשויות בישראל (פיילוט: תל אביב).`,
    analysisStatus: {
      complete: 'הושלם',
      'needs-review': 'נדרשת בדיקה',
      error: 'נדרשת בדיקה',
      'not-run': 'טרם בוצע',
    },
    progressLabel: 'התקדמות תהליך הבדיקה',
  },
};
